/**
 * Normalizes video/trailer URLs for playback in the browser.
 * If the URL is an HLS playlist or asset hosted on BunnyCDN (b-cdn.net),
 * routes it through our internal streaming proxy (/api/stream) so the
 * required Referer header (https://kast.ng/) is attached and 403 Forbidden is avoided.
 */
export function normalizeVideoUrl(url) {
  if (!url || typeof url !== 'string') return null;

  const trimmed = url.trim();
  if (!trimmed) return null;

  if (trimmed.startsWith('/api/stream/')) return trimmed;

  if (trimmed.includes('b-cdn.net')) {
    const withoutProtocol = trimmed.replace(/^https?:\/\//, '');
    return `/api/stream/${withoutProtocol}`;
  }

  return trimmed;
}

/**
 * Checks if a URL is a direct video file or HLS stream.
 */
export function isDirectVideoUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /\.(mp4|webm|ogg|m3u8)(\?|$)/i.test(url) || url.includes('/api/stream/');
}

/**
 * Derives an episode's poster from its own video, with no backend/upload step needed: Bunny
 * Stream auto-generates a frame grab for every video it hosts at "<videoId>/thumbnail.jpg",
 * a sibling file next to "<videoId>/playlist.m3u8". Since that URL carries the same signed
 * token (Bunny's CDN token auth is scoped to the whole "<videoId>/" directory, not just the
 * playlist file), swapping the filename keeps it valid without re-signing anything.
 * Returns null for non-Bunny/non-HLS sources, which have no such frame to fall back to.
 */
export function deriveThumbnailFromVideoUrl(videoUrl) {
  if (!videoUrl || typeof videoUrl !== 'string') return null;
  if (!videoUrl.includes('/playlist.m3u8')) return null;
  return videoUrl.replace('/playlist.m3u8', '/thumbnail.jpg');
}
